import axios, { isAxiosError } from 'axios';

const CODE_MESSAGES: Record<string, string> = {
  no_location_selected: 'Select a location before continuing.',
};

function firstFieldError(data: Record<string, unknown>): string | undefined {
  for (const value of Object.values(data)) {
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
      return value[0];
    }
  }
  return undefined;
}

export function getErrorStatus(error: unknown): number | undefined {
  if (axios.isAxiosError(error)) return error.response?.status;
  return undefined;
}

export function errorStatus(error: unknown): number | null {
  if (isAxiosError(error)) return error.response?.status ?? null;
  return null;
}

export function isClientError(error: unknown): boolean {
  const status = errorStatus(error);
  return status != null && status >= 400 && status < 500;
}

export function extractApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | string | undefined;

    if (typeof data === 'string' && data.trim()) return data;

    if (data && typeof data === 'object') {
      const nested = data.error as Record<string, unknown> | string | undefined;
      if (typeof nested === 'string') return nested;
      if (nested && typeof nested === 'object') {
        const msg = nested.message ?? nested.code;
        if (typeof msg === 'string') return msg;
      }

      const code = typeof data.error === 'string' ? data.error : undefined;
      const detail = typeof data.detail === 'string' ? data.detail : undefined;
      const message = typeof data.message === 'string' ? data.message : undefined;

      if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];
      if (detail) return detail;
      if (message) return message;
      if (code) return code;

      const field = firstFieldError(data);
      if (field) return field;
    }

    return error.message || 'Request failed';
  }

  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}

export function getErrorMessage(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  const extracted = extractApiErrorMessage(error);
  if (extracted !== 'Something went wrong' && extracted !== 'Request failed') return extracted;

  if (axios.isAxiosError(error)) {
    if (getErrorStatus(error) === 403) return 'You do not have access to this. Please contact an admin.';
    if (getErrorStatus(error) === 404) return 'This item could not be found.';
    if (error.code === 'ECONNABORTED') return 'The request timed out. Please try again.';
    if (error.message) return error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function isRetriableError(error: unknown): boolean {
  const status = getErrorStatus(error);
  if (status === undefined) return true;
  if (status === 408 || status === 429) return true;
  return status >= 500;
}
